import { memo, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { BarChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import ReactEchart from '../../../../components/common/ReactEchart';
import { useAppContext } from '../../../../providers/AppProvider';
import StatisticsCardLoading from '../../common/statistic-chart-card/StatisticsChartCardLoading';
import StatisticsChartEmpty from '../../common/StatisticsChartEmpty';
import { STATISTICS_EMPTY_CHART } from '../../common/lib/statisticsEmptyStateCopy';
import useStatisticsChartTheme from '../../common/hooks/useStatisticsChartTheme';
import { getStatisticsAccentColor } from '../../common/lib/statisticsChartPalette';
import StatisticsChartFrame from '../../common/StatisticsChartFrame';
import {
	buildPrimaryPhaseMetricTooltipLine,
	formatPrimaryPhaseMetricAxisValue,
	formatPrimaryPhaseMetricValue,
	getPrimaryPhaseMetricLabel,
	getPrimaryPhaseRowMetricValue,
	isPrimaryPhaseCurrencyMetric,
} from './lib/orderPrimaryPhaseMetric';
import { getPrimaryPhaseChartTheme } from './lib/orderPrimaryPhaseChartTheme';
import { buildDashboardPresetPlotLoadingStyle } from '../board/lib/dashboardPresetLayout';
import { applyChartSummaryLegend } from '../shared/lib/chartSummaryLegend';
import { resolveBarBorderRadius, resolveBarLabelPosition } from '../shared/lib/barOrientation';

echarts.use([TooltipComponent, GridComponent, LegendComponent, BarChart, CanvasRenderer]);

const OrderPrimaryPhaseDimensionChart = ({
	rows = [],
	isLoading = false,
	activeMetric = 'count',
	selectedKey = null,
	onSelectRow,
	animationKey = 'default',
	barHeight = '14rem',
	topN = 12,
	fillSlot = false,
	summarySalesTotal = 0,
	summaryOrderCount = 0,
}) => {
	const { getThemeColor } = useAppContext();
	const { isLight } = useStatisticsChartTheme();
	const chartTheme = useMemo(() => getPrimaryPhaseChartTheme(isLight), [isLight]);
	const metricLabel = getPrimaryPhaseMetricLabel(activeMetric);
	const currencyMetric = isPrimaryPhaseCurrencyMetric(activeMetric);
	const accentColor = useMemo(() => getStatisticsAccentColor(isLight, getThemeColor), [getThemeColor, isLight]);
	const chartRef = useRef(null);

	const chartRows = useMemo(() => {
		const sorted = [...rows].sort((a, b) => getPrimaryPhaseRowMetricValue(b, activeMetric) - getPrimaryPhaseRowMetricValue(a, activeMetric));
		return sorted.slice(0, topN);
	}, [activeMetric, rows, topN]);

	const handleClick = useCallback(
		(params) => {
			const key = params?.data?.key;
			if (key && onSelectRow) onSelectRow(key);
		},
		[onSelectRow],
	);

	const chartEvents = useMemo(() => ({ click: handleClick }), [handleClick]);
	const frameStyle = useMemo(() => (fillSlot ? { height: '100%', minHeight: 0 } : { height: barHeight, minHeight: barHeight }), [barHeight, fillSlot]);
	const chartStyle = useMemo(
		() => (fillSlot ? { width: '100%', height: '100%', minHeight: 0 } : { width: '100%', height: barHeight, minHeight: barHeight }),
		[barHeight, fillSlot],
	);

	useLayoutEffect(() => {
		if (!fillSlot) return undefined;
		const frameId = requestAnimationFrame(() => {
			try {
				chartRef.current?.getEchartsInstance?.()?.resize();
			} catch {
				// 弹层切换时实例可能尚未挂载
			}
		});
		return () => cancelAnimationFrame(frameId);
	}, [fillSlot, animationKey, chartRows.length, activeMetric]);

	const barOption = useMemo(
		() =>
			applyChartSummaryLegend(
				{
					backgroundColor: 'transparent',
					grid: {
						left: 4,
						right: fillSlot ? 12 : 48,
						top: fillSlot ? 28 : 36,
						bottom: fillSlot ? 4 : 8,
						containLabel: true,
					},
					tooltip: {
						...chartTheme.tooltip,
						trigger: 'axis',
						axisPointer: { type: 'shadow', shadowStyle: { color: chartTheme.axisPointerShadow } },
						formatter: (items) => {
							const item = items?.[0];
							if (!item) return '';
							const row = chartRows[item.dataIndex];
							return [row.label, buildPrimaryPhaseMetricTooltipLine(row, activeMetric).replace(/<br\/>/g, '<br/>')].join('<br/>');
						},
					},
					xAxis: {
						type: 'value',
						...(currencyMetric ? {} : { minInterval: 1 }),
						...chartTheme.axis,
						axisLabel: {
							...chartTheme.axis.axisLabel,
							fontSize: 9,
							formatter: currencyMetric ? formatPrimaryPhaseMetricAxisValue : undefined,
						},
					},
					yAxis: {
						type: 'category',
						data: chartRows.map((row) => row.label),
						inverse: true,
						...chartTheme.axis,
						axisLabel: {
							...chartTheme.axis.axisLabel,
							fontSize: 10,
							interval: 0,
							width: fillSlot ? 72 : 88,
							overflow: 'truncate',
						},
						splitLine: { show: false },
					},
					series: [
						{
							name: metricLabel,
							type: 'bar',
							data: chartRows.map((row) => ({
								value: getPrimaryPhaseRowMetricValue(row, activeMetric),
								key: row.key,
								itemStyle: {
									...chartTheme.createBarStyle(accentColor, selectedKey, row.key),
									borderRadius: resolveBarBorderRadius(true),
								},
							})),
							barMaxWidth: 18,
							emphasis: chartTheme.barEmphasis,
							label: {
								show: !fillSlot,
								position: resolveBarLabelPosition(true),
								fontSize: 9,
								color: chartTheme.labelColor,
								formatter: (params) => formatPrimaryPhaseMetricValue(params.value, activeMetric),
							},
							animationDuration: 500,
							animationEasing: 'cubicOut',
						},
					],
				},
				{ salesTotal: summarySalesTotal, orderCount: summaryOrderCount },
				chartTheme,
			),
		[accentColor, activeMetric, chartRows, chartTheme, currencyMetric, fillSlot, metricLabel, selectedKey, summaryOrderCount, summarySalesTotal],
	);

	if (isLoading) {
		return (
			<StatisticsCardLoading
				className={['statistics-lite-chart__loading', fillSlot ? 'statistics-lite-chart__loading--fill' : null].filter(Boolean).join(' ')}
				style={buildDashboardPresetPlotLoadingStyle(barHeight, { fillSlot })}
				fill={fillSlot}
			/>
		);
	}

	if (!chartRows.length) {
		return <StatisticsChartEmpty>{STATISTICS_EMPTY_CHART}</StatisticsChartEmpty>;
	}

	return (
		<StatisticsChartFrame fillSlot={fillSlot} style={frameStyle}>
			<ReactEchart ref={chartRef} key={`dimension-bar-${animationKey}`} echarts={echarts} option={barOption} onEvents={chartEvents} style={chartStyle} />
		</StatisticsChartFrame>
	);
};

OrderPrimaryPhaseDimensionChart.propTypes = {
	rows: PropTypes.array,
	isLoading: PropTypes.bool,
	activeMetric: PropTypes.string,
	selectedKey: PropTypes.string,
	onSelectRow: PropTypes.func,
	animationKey: PropTypes.string,
	barHeight: PropTypes.string,
	topN: PropTypes.number,
	fillSlot: PropTypes.bool,
	summarySalesTotal: PropTypes.number,
	summaryOrderCount: PropTypes.number,
};

export default memo(OrderPrimaryPhaseDimensionChart);
